import { Component } from '@theme/component';

/**
 * AJAX Shopify contact form for bulk order requests.
 * Submits without a full page reload when Shopify allows it.
 * Falls back to a native submit for bot-protection / challenge responses.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [fields]
 * @property {HTMLElement} [success]
 * @property {HTMLElement} [error]
 * @property {HTMLElement} [errorMessage]
 * @property {HTMLButtonElement} [submit]
 *
 * @extends {Component<Refs>}
 */
class BulkOrderFormComponent extends Component {
  /** @type {string} */
  #idleLabel = '';

  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    super.connectedCallback();

    this.#form = this.querySelector('form');
    this.#form?.addEventListener('submit', this.handleSubmit);

    const { submit } = this.refs;
    if (submit) this.#idleLabel = submit.textContent?.trim() || '';
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.handleSubmit);
    super.disconnectedCallback();
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const form = this.#form;
    const { submit, error } = this.refs;
    if (!(form instanceof HTMLFormElement)) return;
    if (this.dataset.state === 'submitting') return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    this.#setState('submitting');
    this.#hide(error);
    if (submit) {
      submit.disabled = true;
      submit.textContent = this.dataset.submittingLabel || 'Sending…';
    }

    try {
      const formData = new FormData(form);
      if (!formData.get('form_type')) {
        formData.set('form_type', 'contact');
      }

      const action = this.#getAction(form);
      const response = await fetch(action, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        // Needed to read redirect Location for success / challenge flows.
        redirect: 'manual',
      });

      if (this.#isSuccessfulRedirect(response)) {
        this.#showSuccess();
        return;
      }

      // Bot protection / rate limit — let Shopify serve the challenge page.
      if (response.status === 400 || response.status === 429) {
        this.#nativeSubmit(form);
        return;
      }

      if (response.type === 'opaqueredirect') {
        this.#showSuccess();
        return;
      }

      if (response.ok) {
        const html = await response.text();
        if (this.#htmlLooksSuccessful(html)) {
          this.#showSuccess();
          return;
        }
      }

      const location = response.headers.get('Location') || '';
      if (location.includes('/challenge')) {
        window.location.assign(location);
        return;
      }

      this.#showError(this.dataset.errorLabel || 'Something went wrong. Please try again.');
    } catch {
      // Network / unexpected failure — fall back to a normal form post.
      this.#nativeSubmit(form);
    } finally {
      if (this.dataset.state !== 'success' && this.dataset.state !== 'submitting' && submit) {
        submit.disabled = false;
        submit.textContent = this.#idleLabel;
      }
    }
  };

  /**
   * @param {HTMLFormElement} form
   */
  #getAction(form) {
    const raw = form.getAttribute('action') || `${window.Shopify?.routes?.root || '/'}contact`;
    try {
      const url = new URL(raw, window.location.origin);
      url.hash = '';
      return url.pathname + url.search;
    } catch {
      return raw.split('#')[0];
    }
  }

  /**
   * @param {Response} response
   */
  #isSuccessfulRedirect(response) {
    if (![301, 302, 303, 307, 308].includes(response.status)) return false;

    const location = response.headers.get('Location') || '';
    return location.includes('contact_posted=true');
  }

  /**
   * @param {HTMLFormElement} form
   */
  #nativeSubmit(form) {
    form.removeEventListener('submit', this.handleSubmit);
    HTMLFormElement.prototype.submit.call(form);
  }

  #showSuccess() {
    const { fields, success, submit } = this.refs;
    this.#setState('success');
    this.#hide(fields);
    this.#show(success);
    this.#form?.reset();
    if (submit) {
      submit.disabled = false;
      submit.textContent = this.#idleLabel;
    }
    success?.focus?.();
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const { error, errorMessage, fields, submit } = this.refs;
    this.#setState('error');
    this.#show(fields);
    if (errorMessage) errorMessage.textContent = message;
    this.#show(error);
    if (submit) {
      submit.disabled = false;
      submit.textContent = this.#idleLabel;
    }
    error?.focus?.();
  }

  /**
   * @param {'idle' | 'submitting' | 'success' | 'error'} state
   */
  #setState(state) {
    this.dataset.state = state;
  }

  /**
   * @param {Element | undefined} el
   */
  #show(el) {
    if (!(el instanceof HTMLElement)) return;
    el.hidden = false;
  }

  /**
   * @param {Element | undefined} el
   */
  #hide(el) {
    if (!(el instanceof HTMLElement)) return;
    el.hidden = true;
  }

  /**
   * @param {string} html
   */
  #htmlLooksSuccessful(html) {
    return (
      html.includes('contact_posted=true') ||
      html.includes('form-status-success') ||
      html.includes('contact-form__success') ||
      html.includes('product-volume-callout__form-success')
    );
  }
}

if (!customElements.get('bulk-order-form')) {
  customElements.define('bulk-order-form', BulkOrderFormComponent);
}
